import React from 'react';
import { PostType } from '../types/postTypes';
import Badge from './bootstrap/Badge';
import Icon from './icon/Icon';

interface PostTypeIndicatorProps {
  postType?: PostType;
  showIcon?: boolean;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

/**
 * PostTypeIndicator Component
 * Shows a visual indicator for different post types
 */
const PostTypeIndicator: React.FC<PostTypeIndicatorProps> = ({
  postType = PostType.NORMAL_POST,
  showIcon = true,
  showLabel = true,
  size = 'sm',
  className = ''
}) => {
  const getPostTypeConfig = (type: PostType) => {
    switch (type) {
      case PostType.VIDEO_POST:
        return {
          icon: 'PlayCircle',
          label: 'Video',
          color: 'danger',
          bgColor: '#dc3545'
        };
      case PostType.IMAGE_POST:
        return {
          icon: 'Image',
          label: 'Image',
          color: 'info',
          bgColor: '#0dcaf0'
        };
      case PostType.MAGAZINE_POST:
        return {
          icon: 'MenuBook',
          label: 'Magazine',
          color: 'success',
          bgColor: '#198754'
        };
      case PostType.CAROUSEL_POST:
        return {
          icon: 'ViewCarousel',
          label: 'Carousel',
          color: 'warning',
          bgColor: '#ffc107'
        };
      case PostType.OPINION_POLL:
        return {
          icon: 'Poll',
          label: 'Poll',
          color: 'primary',
          bgColor: '#0d6efd'
        };
      case PostType.ADVERTISEMENT_POST:
        return {
          icon: 'Ads',
          label: 'Ad',
          color: 'dark',
          bgColor: '#212529'
        };
      case PostType.PRODUCT_POST:
        return {
          icon: 'ShoppingCart',
          label: 'Product',
          color: 'success',
          bgColor: '#198754'
        };
      case PostType.PODCAST_POST:
        return {
          icon: 'Headphones',
          label: 'Podcast',
          color: 'info',
          bgColor: '#1DB954' // Spotify green for podcast
        };
      case PostType.NORMAL_POST:
      default:
        return {
          icon: 'Article',
          label: 'Normal',
          color: 'secondary',
          bgColor: '#6c757d'
        };
    }
  };

  const config = getPostTypeConfig(postType);

  if (!showIcon && !showLabel) {
    return null;
  }

  const badgeSize = size === 'lg' ? undefined : size;

  return (
    <Badge 
      color={config.color as any} 
      isLight 
      className={`d-inline-flex align-items-center gap-1 ${className}`}
      style={{
        fontSize: size === 'lg' ? '0.875rem' : size === 'sm' ? '0.75rem' : '0.8rem'
      }}
    >
      {showIcon && (
        <Icon 
          icon={config.icon} 
          size={size === 'lg' ? 'md' : 'sm'} 
          style={{ color: config.bgColor }}
        />
      )}
      {showLabel && (
        <span style={{ 
          fontSize: size === 'lg' ? '0.875rem' : size === 'sm' ? '0.75rem' : '0.8rem',
          fontWeight: 500
        }}>
          {config.label}
        </span>
      )}
    </Badge>
  );
};

export default PostTypeIndicator;
